import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useParams, Link, useHistory } from "react-router-dom";
import { Modal } from 'react-bootstrap';
import { PageHeader } from '../components/ui/bootstrap';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import PodcastEpisodeSettings from '../components/PodcastEpisodeSettings';
import Spinner from '../components/Spinner';
import AddAudioToPodcastModal from '../components/AddAudioToPodcastModal';
import AudioPlayer from '../components/AudioPlayer';
import PodcastEpisodeItem from '../components/podcast/PodcastEpisodeItem';
import TextRowListSkeleton from '../components/skeletons/pages/TextRowListSkeleton';

import {
    usePodcast,
    usePodcastEpisodes,
    usePublishEpisode,
    useUnpublishEpisode,
    useDeleteEpisode
} from '../http-hooks/podcast/usePodcast';

import copyIcon from '../assets/copy-alt.svg';
import leftArrowIcon from '../assets/arrow-left.svg';

function PodcastAudios() {

    const { org_id, id, podcast_id } = useParams();
    const history = useHistory();

    const [showEpisodeSettings, setShowEpisodeSettings] = useState(false);
    const [selectedEpisode, setSelectedEpisode] = useState(null);
    const [showEmbedCode, setShowEmbedCode] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showAudiosModal, setShowAudiosModal] = useState(false);
    const [audio, setAudio] = useState(null);
    const [audioTitle, setAudioTitle] = useState("Press play to listen to this.")
    const [embedCode, setEmbedCode] = useState('');

    // React Query hooks
    const { data: showDetails } = usePodcast({ org_id, integration_id: id, podcast_id });
    const {
        data: episodesData,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = usePodcastEpisodes({ org_id, integration_id: id, podcast_id });

    const publishEpisodeMutation = usePublishEpisode({ org_id, integration_id: id, podcast_id });
    const unpublishEpisodeMutation = useUnpublishEpisode({ org_id, integration_id: id, podcast_id });
    const deleteEpisodeMutation = useDeleteEpisode({ org_id, integration_id: id, podcast_id });

    // Flatten paginated episodes data
    const episodes = episodesData?.pages?.flatMap(page => page.data) ?? [];

    const publishEpisode = (index) => {
        const episode = episodes[index];
        publishEpisodeMutation.mutate(episode.content_id);
    }

    const unpublishEpisode = (index) => {
        const episode = episodes[index];
        unpublishEpisodeMutation.mutate(episode.content_id);
    }

    const deleteEpisode = async () => {
        await deleteEpisodeMutation.mutateAsync(selectedEpisode.content_id);
        setShowConfirmation(false);
    }

    const EmptyState = () => {
        return (
            <>
                <h5 style={{ marginTop: '32px' }}>This show does not have any episodes.</h5>
                <p>Start by adding episodes from your existing audios.</p>
                <button onClick={() => setShowAudiosModal(true)} className="btn btn-primary">Add Episodes</button>
            </>
        );
    }

    const EpisodesList = () => {
        return (
            <>
                <ul className="list-group list-group-flush" style={{ marginTop: '16px' }}>
                    {
                        episodes.map((item, index) => {
                            return (
                                <PodcastEpisodeItem
                                    key={`episode-${index}`}
                                    item={item}
                                    index={index}
                                    org_id={org_id}
                                    id={id}
                                    podcast_id={podcast_id}
                                    showDetails={showDetails}
                                    setAudio={setAudio}
                                    setAudioTitle={setAudioTitle}
                                    setSelectedEpisode={setSelectedEpisode}
                                    setSelectedEpisodeIndex={() => { }}
                                    setShowEpisodeSettings={setShowEpisodeSettings}
                                    setEmbedCode={setEmbedCode}
                                    setShowEmbedCode={setShowEmbedCode}
                                    setShowConfirmation={setShowConfirmation}
                                    publishEpisode={publishEpisode}
                                    unpublishEpisode={unpublishEpisode}
                                />
                            );
                        })
                    }
                </ul>
                {
                    isFetchingNextPage ? (
                        <button className={classNames("btn--fetch-more", "mt-3")}>
                            <Spinner color="var(--bs-secondary-color)" />
                        </button>
                    ) : hasNextPage ? (
                        <button className={classNames("btn--fetch-more", "mt-3")} onClick={() => fetchNextPage()}>
                            Fetch More
                        </button>
                    ) : null
                }
            </>
        );
    };

    return (
        <>
            <PageHeader
                title={showDetails ? showDetails.title + ' - Episodes' : ''}
                left={
                    <div
                        className="d-flex align-items-center gap-1"
                        style={{ color: 'var(--blue)', cursor: 'pointer', fontSize: 13 }}
                        onClick={() => history.push(`/organisation/${org_id}/integration/${id}/podcast`)}
                    >
                        <img src={leftArrowIcon} width={16} alt="Back" />
                        Back
                    </div>
                }
                right={
                    episodes.length >= 0 && showDetails && !showDetails.is_automatic
                        ? (
                            <Link to={`/organisation/${org_id}/integration/${id}/podcast/${podcast_id}/episodes-add`}>
                                <button className="btn--new-episode">Add New Episodes</button>
                            </Link>
                        )
                        : null
                }
            />

            <AddAudioToPodcastModal
                show={showAudiosModal}
                orgId={org_id}
                integrationId={id}
                podcastId={podcast_id}
                onHide={() => setShowAudiosModal(false)}
                showDetails={showDetails}
                onSuccess={() => {
                    // Episodes will automatically refetch via query invalidation
                }}
            />

            <PodcastEpisodeSettings
                show={showEpisodeSettings}
                onHide={() => setShowEpisodeSettings(false)}
                orgId={org_id}
                integrationId={id}
                podcastId={podcast_id}
                isNew={false}
                selectedEpisode={selectedEpisode}
                onSuccess={(data) => {
                    // Episodes will automatically refetch via query invalidation
                    setShowEpisodeSettings(false);
                }}
            />

            <Modal className="modal-embed-code" show={showEmbedCode} onHide={() => setShowEmbedCode(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Embed Code
                        <p style={{ fontSize: 16, fontWeight: 400, margin: 0 }}>Add the following line to your website’s HTML code as per below.</p>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CopyToClipboard text={embedCode} onCopy={() => toast.success("Copied to clipboard")}>
                        <button
                            type="button"
                            className="btn btn-outline-primary position-absolute d-flex align-items-center gap-2"
                            style={{
                                right: 16,
                                top: 16,
                            }}
                        >
                            <img src={copyIcon} width="16" alt="copy icon" />
                            <span>Copy</span>
                        </button>
                    </CopyToClipboard>
                    <SyntaxHighlighter className="pt-3 pt-sm-0" language="html">
                        {embedCode}
                    </SyntaxHighlighter>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-primary me-3" onClick={() => setShowEmbedCode(false)}>Close</button>
                </Modal.Footer>
            </Modal>

            <Modal show={showConfirmation} onHide={() => { setShowConfirmation(false) }}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Episode</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this episode?</p>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-primary me-3" onClick={() => setShowConfirmation(false)}>Cancel</button>
                    {
                        deleteEpisodeMutation.isLoading ? (
                            <button type="button" className="btn btn-danger text-center">
                                <Spinner color="white" />
                            </button>
                        ) : (
                            <button type="button" className="btn btn-danger" onClick={() => deleteEpisode()}>Delete</button>
                        )
                    }
                </Modal.Footer>
            </Modal>

            {
                isLoading
                    ?
                    (
                        <TextRowListSkeleton />
                    )
                    :
                    (
                        episodes.length === 0
                            ?
                            (
                                <EmptyState />
                            )
                            :
                            (
                                <EpisodesList />
                            )
                    )
            }
            {
                (audio) &&
                <div className="audio-player-wrapper">
                    <AudioPlayer audio={audio} title={audioTitle} onClose={() => setAudio(null)} />
                </div>
            }
        </>
    );
}

export default PodcastAudios;
